import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { extractErrorMessage } from 'helpers/extractErrorMessage';
import { array, bool, enforce, num, object, oneOf, optional, runSchema, str, unknownValue } from 'validation';
import { buildConfirmImportToastMessage, buildRescheduleToastMessage, countInstancesWithMissingMandatoryParameters } from './transportsImportHelpers';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface TransportItem {
  id: number;
  transport_id: number;
  object_type:
    | 'node_definition'
    | 'lock'
    | 'trigger'
    | 'event'
    | 'scheduling_window'
    | 'queue'
    | 'zone'
    | 'folder'
    | 'connection'
    | 'worker'
    | 'config_item'
    | 'recurrence_definition'
    | 'integration'
    | 'python_library'
    | 'role'
    | 'user';
  object_id: number;
  object_name?: string | null;
  created_at?: string;
  inserted_count?: number;
  requested_count?: number;
}

export interface Transport {
  id: number;
  name: string;
  description?: string;
  item_count?: number;
  items?: TransportItem[];
  created_at?: string;
  updated_at?: string;
}

export interface TransportDestination {
  id: number;
  name: string;
  transporter_node_definition_id: number;
  transporter_node_definition_name?: string;
  transporter_zone_name?: string;
  created_at?: string;
  updated_at?: string;
}

export interface TransportDestinationSubmitResult {
  message: string;
  node_instance: {
    node_instance_id: number;
    status: string;
  };
  transport_destination_id: number;
  transport_destination_name: string;
  transport_id: number;
  transporter_node_definition_id: number;
  transporter_node_definition_name?: string;
  transporter_zone_name?: string;
}

export interface TransportImport {
  id: number;
  filename: string;
  status: 'pending' | 'success' | 'failed';
  notes?: string;
  preview_data?: {
    valid: boolean;
    preview: ImportPreviewItem[];
    errors: string[];
  } | null;
  created_by?: number;
  created_at: string;
  updated_at?: string;
}

export interface ImportPreviewChange {
  field: string;
  old: unknown;
  new: unknown;
}

export interface ImportPreviewItem {
  entryPath: string;
  object_type: string;
  name: string;
  status: 'new' | 'overwrite' | 'error';
  reason?: string;
  changes?: ImportPreviewChange[];
}

export interface ImportPreviewResult {
  test_mode: boolean;
  valid: boolean;
  preview: ImportPreviewItem[];
  errors: string[];
  confirm_token?: string;
}

export interface ScheduledInstance {
  node_instance_id: number;
  instance_name: string;
  status: string;
  definition_id: number;
  definition_name: string;
  scheduled_start_datetime: string;
  flow_instance_id: number | null;
  zone_name: string;
  /** Present when the imported definition has mandatory params still without a value. */
  missing_mandatory_parameters?: string[];
}

export interface RescheduleInstanceResult {
  node_instance_id: number;
  status: string;
  instance_name?: string;
  reason?: string;
  warnings?: Array<{
    code: string;
    message: string;
    missing_mandatory_parameters?: Array<{ node_instance_id: number; parameters: string[] }>;
  }>;
}

export interface ImportConfirmResult {
  message: string;
  import_id: number;
  scheduled_instances: ScheduledInstance[];
}

interface TransportsState {
  transports: Transport[];
  selectedTransport: Transport | null;
  transportDestinations: TransportDestination[];
  imports: TransportImport[];
  importPreview: ImportPreviewResult | null;
  scheduledInstances: ScheduledInstance[];
  loading: {
    fetchTransports: boolean;
    fetchTransport: boolean;
    createTransport: boolean;
    updateTransport: boolean;
    deleteTransport: boolean;
    addItem: boolean;
    removeItem: boolean;
    fetchTransportDestinations: boolean;
    createTransportDestination: boolean;
    submitTransportDestination: boolean;
    fetchImports: boolean;
    importZip: boolean;
    confirmImport: boolean;
    reschedule: boolean;
  };
  error: string | null;
}

const initialState: TransportsState = {
  transports: [],
  selectedTransport: null,
  transportDestinations: [],
  imports: [],
  importPreview: null,
  scheduledInstances: [],
  loading: {
    fetchTransports: false,
    fetchTransport: false,
    createTransport: false,
    updateTransport: false,
    deleteTransport: false,
    addItem: false,
    removeItem: false,
    fetchTransportDestinations: false,
    createTransportDestination: false,
    submitTransportDestination: false,
    fetchImports: false,
    importZip: false,
    confirmImport: false,
    reschedule: false,
  },
  error: null,
};

const transportDestinationSchema = object<TransportDestination>({
  id: num(),
  name: str(),
  transporter_node_definition_id: num(),
  transporter_node_definition_name: optional(str()),
  transporter_zone_name: optional(str()),
  created_at: optional(str()),
  updated_at: optional(str()),
});

const transportDestinationsListSchema = object<{ transport_destinations: readonly TransportDestination[] }>({
  transport_destinations: array(transportDestinationSchema),
});

const transportDestinationSubmitResultSchema = object<TransportDestinationSubmitResult>({
  message: str(),
  node_instance: object<TransportDestinationSubmitResult['node_instance']>({
    node_instance_id: num(),
    status: str(),
  }),
  transport_destination_id: num(),
  transport_destination_name: str(),
  transport_id: num(),
  transporter_node_definition_id: num(),
  transporter_node_definition_name: optional(str()),
  transporter_zone_name: optional(str()),
});

const missingMandatoryParameterEntrySchema = object<{ node_instance_id: number; parameters: string[] }>({
  node_instance_id: num(),
  parameters: array(str()),
});

const rescheduleWarningSchema = object<NonNullable<RescheduleInstanceResult['warnings']>[number]>({
  code: str(),
  message: str(),
  missing_mandatory_parameters: optional(array(missingMandatoryParameterEntrySchema)),
});

const rescheduleInstanceResultSchema = object<RescheduleInstanceResult>({
  node_instance_id: num(),
  status: str(),
  instance_name: optional(str()),
  reason: optional(str()),
  warnings: optional(array(rescheduleWarningSchema)),
});

const rescheduleInstancesResponseSchema = object<{ results: RescheduleInstanceResult[] }>({
  results: array(rescheduleInstanceResultSchema),
});

const scheduledInstanceSchema = object<ScheduledInstance>({
  node_instance_id: num(),
  instance_name: optional(str()),
  status: str(),
  definition_id: num(),
  definition_name: str(),
  scheduled_start_datetime: optional(str()),
  flow_instance_id: optional(num()),
  zone_name: optional(str()),
  missing_mandatory_parameters: optional(array(str())),
});

const importConfirmResultSchema = object<ImportConfirmResult>({
  message: str(),
  import_id: num(),
  scheduled_instances: array(scheduledInstanceSchema),
});

const importPreviewChangeSchema = object<ImportPreviewChange>({
  field: str(),
  old: unknownValue(),
  new: unknownValue(),
});

const importPreviewItemSchema = object<ImportPreviewItem>({
  entryPath: str(),
  object_type: str(),
  name: str(),
  status: oneOf(['new', 'overwrite', 'error'] as const, 'error'),
  reason: optional(str()),
  changes: optional(array(importPreviewChangeSchema)),
});

const importPreviewResultSchema = object<ImportPreviewResult>({
  test_mode: bool(),
  valid: bool(),
  preview: array(importPreviewItemSchema),
  errors: array(str()),
  confirm_token: optional(str()),
});

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------
export const fetchTransports = createAsyncThunk<Transport[], void, { rejectValue: string }>('transports/fetchTransports', async (_, { rejectWithValue }) => {
  try {
    const res = await api.get('/transports');
    return res.data?.transports ?? [];
  } catch (err: any) {
    return rejectWithValue(err?.response?.data?.error || err?.message || 'Failed to fetch transports');
  }
});

export const fetchTransport = createAsyncThunk<Transport, number, { rejectValue: string }>('transports/fetchTransport', async (id, { rejectWithValue }) => {
  try {
    const res = await api.get(`/transports/${id}`);
    return res.data as Transport;
  } catch (err: any) {
    return rejectWithValue(err?.response?.data?.error || err?.message || 'Failed to fetch transport');
  }
});

export const createTransport = createAsyncThunk<Transport, { name: string; description?: string }, { rejectValue: string }>(
  'transports/createTransport',
  async (body, { rejectWithValue }) => {
    try {
      const res = await api.post('/transports', body);
      return res.data as Transport;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.error || err?.message || 'Failed to create transport');
    }
  }
);

export const updateTransport = createAsyncThunk<Transport, { id: number; name?: string; description?: string }, { rejectValue: string }>(
  'transports/updateTransport',
  async ({ id, ...body }, { rejectWithValue }) => {
    try {
      const res = await api.put(`/transports/${id}`, body);
      return res.data as Transport;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.error || err?.message || 'Failed to update transport');
    }
  }
);

export const deleteTransport = createAsyncThunk<number, number, { rejectValue: string }>('transports/deleteTransport', async (id, { rejectWithValue }) => {
  try {
    await api.delete(`/transports/${id}`);
    return id;
  } catch (err: any) {
    return rejectWithValue(err?.response?.data?.error || err?.message || 'Failed to delete transport');
  }
});

export const addTransportItem = createAsyncThunk<
  TransportItem,
  {
    transportId: number;
    object_type: TransportItem['object_type'];
    object_id: number;
    include_related_objects?: boolean;
  },
  { rejectValue: string }
>('transports/addItem', async ({ transportId, object_type, object_id, include_related_objects = true }, { rejectWithValue }) => {
  try {
    const res = await api.post(`/transports/${transportId}/items`, {
      object_type,
      object_id,
      include_related_objects,
    });
    return res.data as TransportItem;
  } catch (err: any) {
    return rejectWithValue(err?.response?.data?.error || err?.message || 'Failed to add item');
  }
});

export const removeTransportItem = createAsyncThunk<{ transportId: number; itemId: number }, { transportId: number; itemId: number }, { rejectValue: string }>(
  'transports/removeItem',
  async ({ transportId, itemId }, { rejectWithValue }) => {
    try {
      await api.delete(`/transports/${transportId}/items/${itemId}`);
      return { transportId, itemId };
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.error || err?.message || 'Failed to remove item');
    }
  }
);

export const fetchTransportDestinations = createAsyncThunk<TransportDestination[], void, { rejectValue: string }>(
  'transports/fetchTransportDestinations',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get('/transport_destinations');
      const result = runSchema(transportDestinationsListSchema, res.data, 'fetchTransportDestinations');
      if (!result.ok) {
        return rejectWithValue('fetchTransportDestinations response failed validation');
      }
      return [
        ...enforce(result.value, result.warnings, ['transport_destinations[*].id', 'transport_destinations[*].transporter_node_definition_id'])
          .transport_destinations,
      ];
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error.response?.data, error.message));
    }
  }
);

export const createTransportDestination = createAsyncThunk<
  TransportDestination,
  {
    name: string;
    transporter_node_definition_id: number;
    transporter_node_definition_name?: string;
    transporter_zone_name?: string;
  },
  { rejectValue: string }
>('transports/createTransportDestination', async ({ transporter_node_definition_name, transporter_zone_name, ...body }, { rejectWithValue }) => {
  try {
    const res = await api.post('/transport_destinations', body);
    const result = runSchema(transportDestinationSchema, res.data, 'createTransportDestination');
    if (!result.ok) {
      return rejectWithValue('createTransportDestination response failed validation');
    }
    const destination = enforce(result.value, result.warnings, ['id', 'transporter_node_definition_id']);
    return {
      ...destination,
      ...(transporter_node_definition_name ? { transporter_node_definition_name } : {}),
      ...(transporter_zone_name ? { transporter_zone_name } : {}),
    };
  } catch (error: any) {
    return rejectWithValue(extractErrorMessage(error.response?.data, error.message));
  }
});

export const submitTransportDestination = createAsyncThunk<
  TransportDestinationSubmitResult,
  {
    transportDestinationId: number;
    transport_id: number;
    description: string;
  },
  { rejectValue: string }
>('transports/submitTransportDestination', async ({ transportDestinationId, transport_id, description }, { rejectWithValue }) => {
  try {
    const res = await api.post(`/transport_destinations/${transportDestinationId}/submit`, { transport_id, description });
    const result = runSchema(transportDestinationSubmitResultSchema, res.data, 'submitTransportDestination');
    if (!result.ok) {
      return rejectWithValue('submitTransportDestination response failed validation');
    }
    return enforce(result.value, result.warnings, [
      'transport_destination_id',
      'transport_id',
      'transporter_node_definition_id',
      'node_instance.node_instance_id',
    ]);
  } catch (error: any) {
    return rejectWithValue(extractErrorMessage(error.response?.data, error.message));
  }
});

export const fetchTransportImports = createAsyncThunk<TransportImport[], void, { rejectValue: string }>(
  'transports/fetchImports',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get('/transports/imports');
      return res.data?.imports ?? [];
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.error || err?.message || 'Failed to fetch imports');
    }
  }
);

export const importTransportZip = createAsyncThunk<
  ImportPreviewResult,
  { file: File; nameOverrides?: Record<string, string>; testMode?: boolean },
  { rejectValue: string }
>('transports/importZip', async ({ file, nameOverrides, testMode = false }, { rejectWithValue }) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('test_mode', String(testMode));
    if (nameOverrides && Object.keys(nameOverrides).length > 0) {
      formData.append('name_overrides', JSON.stringify(nameOverrides));
    }
    const res = await api.post('/transports/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const result = runSchema(importPreviewResultSchema, res.data, 'importTransportZip');
    if (!result.ok) {
      return rejectWithValue('importTransportZip response failed validation');
    }
    return enforce(result.value, result.warnings, ['preview[*].entryPath']);
  } catch (err: any) {
    return rejectWithValue(err?.response?.data?.error || err?.message || 'Import failed');
  }
});

export const confirmImport = createAsyncThunk<ImportConfirmResult, { confirmToken: string }, { rejectValue: string }>(
  'transports/confirmImport',
  async ({ confirmToken }, { rejectWithValue }) => {
    try {
      const res = await api.post('/transports/import/confirm', {
        confirm_token: confirmToken,
      });
      const result = runSchema(importConfirmResultSchema, res.data, 'confirmImport');
      if (!result.ok) {
        return rejectWithValue('confirmImport response failed validation');
      }
      return enforce(result.value, result.warnings, ['import_id', 'scheduled_instances[*].node_instance_id']);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.error || err?.message || 'Confirm import failed');
    }
  }
);

export const rescheduleInstances = createAsyncThunk<
  {
    results: RescheduleInstanceResult[];
  },
  { nodeInstanceIds: number[] },
  { rejectValue: string }
>('transports/rescheduleInstances', async ({ nodeInstanceIds }, { rejectWithValue }) => {
  try {
    const res = await api.post('/transports/import/reschedule', {
      node_instance_ids: nodeInstanceIds,
    });
    const result = runSchema(rescheduleInstancesResponseSchema, res.data, 'rescheduleInstances');
    if (!result.ok) {
      return rejectWithValue('rescheduleInstances response failed validation');
    }
    return enforce(result.value, result.warnings, ['results[*].node_instance_id']);
  } catch (err: any) {
    return rejectWithValue(err?.response?.data?.error || err?.message || 'Reschedule failed');
  }
});

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------
const transportsSlice = createSlice({
  name: 'transports',
  initialState,
  reducers: {
    clearSelectedTransport(state) {
      state.selectedTransport = null;
    },
    clearImportPreview(state) {
      state.importPreview = null;
    },
    clearScheduledInstances(state) {
      state.scheduledInstances = [];
    },
  },
  extraReducers: (builder) => {
    // fetchTransports
    builder
      .addCase(fetchTransports.pending, (state) => {
        state.loading.fetchTransports = true;
        state.error = null;
      })
      .addCase(fetchTransports.fulfilled, (state, action) => {
        state.transports = action.payload;
        state.loading.fetchTransports = false;
      })
      .addCase(fetchTransports.rejected, (state, action) => {
        state.loading.fetchTransports = false;
        state.error = action.payload ?? 'Failed to fetch transports';
      });

    // fetchTransport
    builder
      .addCase(fetchTransport.pending, (state) => {
        state.loading.fetchTransport = true;
        state.error = null;
      })
      .addCase(fetchTransport.fulfilled, (state, action) => {
        state.selectedTransport = action.payload;
        state.loading.fetchTransport = false;
      })
      .addCase(fetchTransport.rejected, (state, action) => {
        state.loading.fetchTransport = false;
        state.error = action.payload ?? 'Failed to fetch transport';
      });

    // createTransport
    builder
      .addCase(createTransport.pending, (state) => {
        state.loading.createTransport = true;
      })
      .addCase(createTransport.fulfilled, (state, action) => {
        state.transports.push(action.payload);
        state.loading.createTransport = false;
        toast.success('Transport created!');
      })
      .addCase(createTransport.rejected, (state, action) => {
        state.loading.createTransport = false;
        state.error = action.payload ?? 'Failed to create transport';
        toast.error(`Failed to create transport: ${state.error}`);
      });

    // updateTransport
    builder
      .addCase(updateTransport.pending, (state) => {
        state.loading.updateTransport = true;
      })
      .addCase(updateTransport.fulfilled, (state, action) => {
        const idx = state.transports.findIndex((t) => t.id === action.payload.id);
        if (idx !== -1)
          state.transports[idx] = {
            ...state.transports[idx],
            ...action.payload,
          };
        state.loading.updateTransport = false;
        toast.success('Transport updated!');
      })
      .addCase(updateTransport.rejected, (state, action) => {
        state.loading.updateTransport = false;
        state.error = action.payload ?? 'Failed to update transport';
        toast.error(`Failed to update transport: ${state.error}`);
      });

    // deleteTransport
    builder
      .addCase(deleteTransport.pending, (state) => {
        state.loading.deleteTransport = true;
      })
      .addCase(deleteTransport.fulfilled, (state, action) => {
        state.transports = state.transports.filter((t) => t.id !== action.payload);
        state.loading.deleteTransport = false;
        toast.success('Transport deleted!');
      })
      .addCase(deleteTransport.rejected, (state, action) => {
        state.loading.deleteTransport = false;
        state.error = action.payload ?? 'Failed to delete transport';
        toast.error(`Failed to delete transport: ${state.error}`);
      });

    // addTransportItem
    builder
      .addCase(addTransportItem.pending, (state) => {
        state.loading.addItem = true;
      })
      .addCase(addTransportItem.fulfilled, (state, action) => {
        state.loading.addItem = false;
        if (state.selectedTransport && action.payload?.id) {
          state.selectedTransport.items = [...(state.selectedTransport.items ?? []), action.payload];
        }
        toast.success('Item added to transport!');
      })
      .addCase(addTransportItem.rejected, (state, action) => {
        state.loading.addItem = false;
        state.error = action.payload ?? 'Failed to add item';
        toast.error(`Failed to add item: ${state.error}`);
      });

    // removeTransportItem
    builder
      .addCase(removeTransportItem.pending, (state) => {
        state.loading.removeItem = true;
      })
      .addCase(removeTransportItem.fulfilled, (state, action) => {
        state.loading.removeItem = false;
        if (state.selectedTransport) {
          state.selectedTransport.items = (state.selectedTransport.items ?? []).filter((i) => i.id !== action.payload.itemId);
        }
        toast.success('Item removed from transport!');
      })
      .addCase(removeTransportItem.rejected, (state, action) => {
        state.loading.removeItem = false;
        state.error = action.payload ?? 'Failed to remove item';
        toast.error(`Failed to remove item: ${state.error}`);
      });

    // fetchTransportDestinations
    builder
      .addCase(fetchTransportDestinations.pending, (state) => {
        state.loading.fetchTransportDestinations = true;
      })
      .addCase(fetchTransportDestinations.fulfilled, (state, action) => {
        state.transportDestinations = action.payload;
        state.loading.fetchTransportDestinations = false;
      })
      .addCase(fetchTransportDestinations.rejected, (state, action) => {
        state.loading.fetchTransportDestinations = false;
        state.error = action.payload ?? 'Failed to fetch transport destinations';
      });

    // createTransportDestination
    builder
      .addCase(createTransportDestination.pending, (state) => {
        state.loading.createTransportDestination = true;
      })
      .addCase(createTransportDestination.fulfilled, (state, action) => {
        state.transportDestinations.push(action.payload);
        state.loading.createTransportDestination = false;
        toast.success('Transport destination created!');
      })
      .addCase(createTransportDestination.rejected, (state, action) => {
        state.loading.createTransportDestination = false;
        state.error = action.payload ?? 'Failed to create transport destination';
        toast.error(`Failed to create transport destination: ${state.error}`);
      });

    // submitTransportDestination
    builder
      .addCase(submitTransportDestination.pending, (state) => {
        state.loading.submitTransportDestination = true;
      })
      .addCase(submitTransportDestination.fulfilled, (state, action) => {
        state.loading.submitTransportDestination = false;
        toast.success(`Submitted instance #${action.payload.node_instance.node_instance_id}`);
      })
      .addCase(submitTransportDestination.rejected, (state, action) => {
        state.loading.submitTransportDestination = false;
        state.error = action.payload ?? 'Failed to submit transport destination';
        toast.error(`Transport submit failed: ${state.error}`);
      });

    // fetchTransportImports
    builder
      .addCase(fetchTransportImports.pending, (state) => {
        state.loading.fetchImports = true;
      })
      .addCase(fetchTransportImports.fulfilled, (state, action) => {
        state.imports = action.payload;
        state.loading.fetchImports = false;
      })
      .addCase(fetchTransportImports.rejected, (state, action) => {
        state.loading.fetchImports = false;
        state.error = action.payload ?? 'Failed to fetch imports';
      });

    // importTransportZip
    builder
      .addCase(importTransportZip.pending, (state) => {
        state.loading.importZip = true;
        state.importPreview = null;
      })
      .addCase(importTransportZip.fulfilled, (state, action) => {
        state.loading.importZip = false;
        state.importPreview = action.payload;
      })
      .addCase(importTransportZip.rejected, (state, action) => {
        state.loading.importZip = false;
        state.error = action.payload ?? 'Import failed';
        toast.error(`Import failed: ${state.error}`);
      });

    // confirmImport
    builder
      .addCase(confirmImport.pending, (state) => {
        state.loading.confirmImport = true;
      })
      .addCase(confirmImport.fulfilled, (state, action) => {
        state.loading.confirmImport = false;
        state.importPreview = null;
        state.scheduledInstances = action.payload.scheduled_instances ?? [];
        const message = buildConfirmImportToastMessage(state.scheduledInstances);
        if (state.scheduledInstances.length === 0) {
          toast.success(message);
        } else if (countInstancesWithMissingMandatoryParameters(state.scheduledInstances) > 0) {
          toast.warn(message);
        } else {
          toast.info(message);
        }
      })
      .addCase(confirmImport.rejected, (state, action) => {
        state.loading.confirmImport = false;
        state.error = action.payload ?? 'Confirm import failed';
        toast.error(`Import failed: ${state.error}`);
      });

    // rescheduleInstances
    builder
      .addCase(rescheduleInstances.pending, (state) => {
        state.loading.reschedule = true;
      })
      .addCase(rescheduleInstances.fulfilled, (state, action) => {
        state.loading.reschedule = false;
        state.scheduledInstances = [];
        const results = action.payload.results ?? [];
        const message = buildRescheduleToastMessage(results);
        const hasSkipped = results.some((r) => r.status === 'skipped');
        const hasMandatoryWarnings = results.some((r) => (r.warnings ?? []).some((w) => w.code === 'MISSING_MANDATORY_PARAMETERS'));
        if (hasSkipped || hasMandatoryWarnings) {
          toast.warn(message);
        } else {
          toast.success(message);
        }
      })
      .addCase(rescheduleInstances.rejected, (state, action) => {
        state.loading.reschedule = false;
        state.error = action.payload ?? 'Reschedule failed';
        toast.error(`Reschedule failed: ${state.error}`);
      });
  },
});

export const { clearSelectedTransport, clearImportPreview, clearScheduledInstances } = transportsSlice.actions;
export default transportsSlice.reducer;
